import { z } from 'zod';
import { sanitizeDisplayText } from './sanitize.js';
/**
 * Zod schemas for validating game-specific action payloads.
 * These schemas ensure that client-sent data is properly structured
 * and sanitized before being processed by game logic.
 */
// =============================================================================
// Game ID Schema - must stay in sync with @multipack/games/registry.ts
// Run `pnpm validate-protocol-sync` to verify synchronization
// =============================================================================
/**
 * Valid game IDs. This list MUST match GAME_IDS in @multipack/games/registry.ts.
 * The validate-protocol-sync script enforces this at build time.
 */
export const GameIdSchema = z.enum([
    'trivia',
    'tic-tac-toe',
    'checkers',
    'chess',
    'texas-holdem',
    'prompt-party',
    'sea-strike',
    'hot-takes',
    'tall-tales',
    'word-rush',
    'tank-showdown',
    'word-spies',
    'hidden-roles',
    'block-battle',
    'word-tiles',
]);
/** Get array of valid game IDs for runtime use */
export const VALID_GAME_IDS = GameIdSchema.options;
// Helper: Sanitized string schema for user-generated text
const sanitizedText = (maxLength) => z
    .string()
    .min(1)
    .max(maxLength)
    .transform((text) => sanitizeDisplayText(text, maxLength));
// =============================================================================
// Trivia Action Schemas
// =============================================================================
export const TriviaAnswerSchema = z.object({
    type: z.literal('answer'),
    answerIndex: z.number().int().min(0).max(3),
});
export const TriviaTextAnswerSchema = z.object({
    type: z.literal('text_answer'),
    answerText: sanitizedText(80),
});
export const TriviaUpdateSettingsSchema = z.object({
    type: z.literal('update_settings'),
    questionCount: z.number().int().min(5).max(20).optional(),
    timePerQuestion: z.number().int().min(10000).max(60000).optional(),
});
export const TriviaStartGameSchema = z.object({
    type: z.literal('start_game'),
});
export const TriviaActionSchema = z.discriminatedUnion('type', [
    TriviaAnswerSchema,
    TriviaTextAnswerSchema,
    TriviaUpdateSettingsSchema,
    TriviaStartGameSchema,
]);
// =============================================================================
// Tic-Tac-Toe Action Schemas
// =============================================================================
export const TicTacToeMoveSchema = z.object({
    type: z.literal('move'),
    cellIndex: z.number().int().min(0).max(8),
});
const FirstPlayerSchema = z.enum(['random', 'player1', 'player2']);
const BestOfSchema = z.union([z.literal(1), z.literal(3), z.literal(5)]);
export const TicTacToeUpdateSettingsSchema = z.object({
    type: z.literal('update_settings'),
    firstPlayer: FirstPlayerSchema.optional(),
    bestOf: BestOfSchema.optional(),
});
export const TicTacToeStartGameSchema = z.object({
    type: z.literal('start_game'),
});
export const TicTacToeActionSchema = z.discriminatedUnion('type', [
    TicTacToeMoveSchema,
    TicTacToeUpdateSettingsSchema,
    TicTacToeStartGameSchema,
]);
// =============================================================================
// Checkers Action Schema
// =============================================================================
const RedPlayerSchema = z.enum(['random', 'player1', 'player2']);
export const CheckersMoveSchema = z.object({
    type: z.literal('move'),
    from: z.number().int().min(0).max(63),
    to: z.number().int().min(0).max(63),
});
export const CheckersUpdateSettingsSchema = z.object({
    type: z.literal('update_settings'),
    redPlayer: RedPlayerSchema.optional(),
    bestOf: BestOfSchema.optional(),
});
export const CheckersStartGameSchema = z.object({
    type: z.literal('start_game'),
});
export const CheckersActionSchema = z.discriminatedUnion('type', [
    CheckersMoveSchema,
    CheckersUpdateSettingsSchema,
    CheckersStartGameSchema,
]);
// =============================================================================
// Chess Action Schema
// =============================================================================
const PromotionPieceSchema = z.enum(['queen', 'rook', 'bishop', 'knight']);
const WhitePlayerSchema = z.enum(['random', 'player1', 'player2']);
export const ChessMoveSchema = z.object({
    type: z.literal('move'),
    from: z.number().int().min(0).max(63),
    to: z.number().int().min(0).max(63),
    promotion: PromotionPieceSchema.optional(),
});
export const ChessUpdateSettingsSchema = z.object({
    type: z.literal('update_settings'),
    whitePlayer: WhitePlayerSchema.optional(),
    bestOf: BestOfSchema.optional(),
});
export const ChessStartGameSchema = z.object({
    type: z.literal('start_game'),
});
export const ChessActionSchema = z.discriminatedUnion('type', [
    ChessMoveSchema,
    ChessUpdateSettingsSchema,
    ChessStartGameSchema,
]);
// =============================================================================
// Texas Hold'em Action Schemas
// =============================================================================
export const TexasHoldemFoldSchema = z.object({
    type: z.literal('fold'),
});
export const TexasHoldemCheckSchema = z.object({
    type: z.literal('check'),
});
export const TexasHoldemCallSchema = z.object({
    type: z.literal('call'),
});
export const TexasHoldemRaiseSchema = z.object({
    type: z.literal('raise'),
    amount: z.number().int().positive(),
});
export const TexasHoldemAllInSchema = z.object({
    type: z.literal('all_in'),
});
const StartingChipsSchema = z.union([
    z.literal(500),
    z.literal(1000),
    z.literal(2000),
    z.literal(5000),
]);
const BlindLevelSchema = z.enum(['low', 'medium', 'high']);
export const TexasHoldemUpdateSettingsSchema = z.object({
    type: z.literal('update_settings'),
    startingChips: StartingChipsSchema.optional(),
    blindLevel: BlindLevelSchema.optional(),
});
export const TexasHoldemStartGameSchema = z.object({
    type: z.literal('start_game'),
});
export const TexasHoldemActionSchema = z.discriminatedUnion('type', [
    TexasHoldemFoldSchema,
    TexasHoldemCheckSchema,
    TexasHoldemCallSchema,
    TexasHoldemRaiseSchema,
    TexasHoldemAllInSchema,
    TexasHoldemUpdateSettingsSchema,
    TexasHoldemStartGameSchema,
]);
// =============================================================================
// Prompt Party Action Schemas
// =============================================================================
// =============================================================================
export const PromptPartyStartGameSchema = z.object({
    type: z.literal('start_game'),
});
const TimerDurationSchema = z.union([
    z.literal(0),
    z.literal(30),
    z.literal(45),
    z.literal(60),
    z.literal(90),
]);
const PackIdSchema = z.enum([
    'australia',
    '90s-kids',
    'tech',
    'memes',
    'christmas',
    'new-years',
    'movies-tv',
    'programming',
    'gaming',
    'office',
    'dating',
    'parenting',
    'road-trip',
    'pets',
    'academia',
    'american',
    'family',
    'tv-tropes',
    'retro-tech',
    'conspiracy',
    'fantasy',
]);
const VictoryConditionSchema = z.enum(['rounds', 'points']);
export const PromptPartyUpdateSettingsSchema = z.object({
    type: z.literal('update_settings'),
    timerDuration: TimerDurationSchema.optional(),
    totalRounds: z.number().int().min(1).max(20).optional(),
    maxCustomCards: z.number().int().min(0).max(10).optional(),
    victoryCondition: VictoryConditionSchema.optional(),
    pointsToWin: z.number().int().min(1).max(20).optional(),
    enabledPacks: z.array(PackIdSchema).optional(),
});
export const PromptPartySubmitAnswerSchema = z.object({
    type: z.literal('submit_answer'),
    cardIds: z.array(z.string()).min(1).max(3),
});
export const PromptPartySubmitCustomSchema = z.object({
    type: z.literal('submit_custom'),
    customTexts: z.array(sanitizedText(200)).min(1).max(3),
});
export const PromptPartySelectWinnerSchema = z.object({
    type: z.literal('select_winner'),
    submissionIndex: z.number().int().min(0),
});
export const PromptPartyRevealNextSchema = z.object({
    type: z.literal('reveal_next'),
});
export const PromptPartyRevealAnswerSchema = z.object({
    type: z.literal('reveal_answer'),
    submissionIndex: z.number().int().min(0),
});
export const PromptPartyRevealAllSchema = z.object({
    type: z.literal('reveal_all'),
});
export const PromptPartyRetractAnswerSchema = z.object({
    type: z.literal('retract_answer'),
});
export const PromptPartyReadyToJudgeSchema = z.object({
    type: z.literal('ready_to_judge'),
});
export const PromptPartyActionSchema = z.discriminatedUnion('type', [
    PromptPartyStartGameSchema,
    PromptPartyUpdateSettingsSchema,
    PromptPartySubmitAnswerSchema,
    PromptPartySubmitCustomSchema,
    PromptPartySelectWinnerSchema,
    PromptPartyRevealNextSchema,
    PromptPartyRevealAnswerSchema,
    PromptPartyRevealAllSchema,
    PromptPartyRetractAnswerSchema,
    PromptPartyReadyToJudgeSchema,
]);
// =============================================================================
// Sea Strike Action Schemas
// =============================================================================
const CoordinateSchema = z.object({
    row: z.number().int().min(0).max(9),
    col: z.number().int().min(0).max(9),
});
const ShipTypeSchema = z.enum(['carrier', 'cruiser', 'destroyer', 'submarine', 'patrol']);
const OrientationSchema = z.enum(['H', 'V']);
export const SeaStrikePlaceShipSchema = z.object({
    type: z.literal('place_ship'),
    shipType: ShipTypeSchema,
    start: CoordinateSchema,
    orientation: OrientationSchema,
});
export const SeaStrikeRemoveShipSchema = z.object({
    type: z.literal('remove_ship'),
    shipType: ShipTypeSchema,
});
export const SeaStrikeReadySchema = z.object({
    type: z.literal('ready'),
});
export const SeaStrikeFireSchema = z.object({
    type: z.literal('fire'),
    target: CoordinateSchema,
});
export const SeaStrikeActionSchema = z.discriminatedUnion('type', [
    SeaStrikePlaceShipSchema,
    SeaStrikeRemoveShipSchema,
    SeaStrikeReadySchema,
    SeaStrikeFireSchema,
]);
// =============================================================================
// Hot Takes Action Schemas
// =============================================================================
export const HotTakesStartGameSchema = z.object({
    type: z.literal('start_game'),
});
const HotTakesTimerDurationSchema = z.union([
    z.literal(0),
    z.literal(30),
    z.literal(45),
    z.literal(60),
    z.literal(90),
]);
const HotTakesCategorySchema = z.enum([
    'workplace',
    'relationships',
    'tech',
    'food',
    'lifestyle',
    'misc',
    'social',
    'money',
    'travel',
    'hypothetical',
    'entertainment',
    'opinions',
    'party',
    'dynamic',
]);
export const HotTakesUpdateSettingsSchema = z.object({
    type: z.literal('update_settings'),
    writingTime: HotTakesTimerDurationSchema.optional(),
    votingTime: HotTakesTimerDurationSchema.optional(),
    totalRounds: z.number().int().min(1).max(20).optional(),
    enabledCategories: z.array(HotTakesCategorySchema).min(1).optional(),
});
export const HotTakesSubmitTakeSchema = z.object({
    type: z.literal('submit_take'),
    text: sanitizedText(500),
});
export const HotTakesSubmitVoteSchema = z.object({
    type: z.literal('submit_vote'),
    submissionPlayerId: z.string().min(1),
});
export const HotTakesNextRoundSchema = z.object({
    type: z.literal('next_round'),
});
export const HotTakesActionSchema = z.discriminatedUnion('type', [
    HotTakesStartGameSchema,
    HotTakesUpdateSettingsSchema,
    HotTakesSubmitTakeSchema,
    HotTakesSubmitVoteSchema,
    HotTakesNextRoundSchema,
]);
// =============================================================================
// Tall Tales Action Schemas
// =============================================================================
export const TallTalesStartGameSchema = z.object({
    type: z.literal('start_game'),
});
const TallTalesTimerDurationSchema = z.union([
    z.literal(30),
    z.literal(45),
    z.literal(60),
    z.literal(90),
]);
const TallTalesCategorySchema = z.enum([
    'food',
    'geography',
    'history',
    'misc',
    'pop_culture',
    'science',
]);
const TallTalesDifficultySchema = z.enum(['easy', 'medium', 'hard']);
export const TallTalesUpdateSettingsSchema = z.object({
    type: z.literal('update_settings'),
    timePerWriting: TallTalesTimerDurationSchema.optional(),
    timePerGuessing: TallTalesTimerDurationSchema.optional(),
    totalRounds: z.number().int().min(1).max(20).optional(),
    enabledCategories: z.array(TallTalesCategorySchema).min(1).optional(),
    enabledDifficulties: z.array(TallTalesDifficultySchema).min(1).optional(),
});
export const TallTalesSubmitFakeSchema = z.object({
    type: z.literal('submit_fake'),
    text: sanitizedText(500),
});
export const TallTalesSubmitGuessSchema = z.object({
    type: z.literal('submit_guess'),
    answerIndex: z.number().int().min(0),
});
export const TallTalesRevealNextSchema = z.object({
    type: z.literal('reveal_next'),
});
export const TallTalesShowResultsSchema = z.object({
    type: z.literal('show_results'),
});
export const TallTalesNextRoundSchema = z.object({
    type: z.literal('next_round'),
});
export const TallTalesPlayAgainSchema = z.object({
    type: z.literal('play_again'),
});
export const TallTalesActionSchema = z.discriminatedUnion('type', [
    TallTalesStartGameSchema,
    TallTalesUpdateSettingsSchema,
    TallTalesSubmitFakeSchema,
    TallTalesSubmitGuessSchema,
    TallTalesRevealNextSchema,
    TallTalesShowResultsSchema,
    TallTalesNextRoundSchema,
    TallTalesPlayAgainSchema,
]);
// =============================================================================
// Word Rush Action Schemas
// =============================================================================
export const WordRushStartGameSchema = z.object({
    type: z.literal('start_game'),
});
export const WordRushSubmitAnswerSchema = z.object({
    type: z.literal('submit_answer'),
    answer: sanitizedText(50),
});
export const WordRushChangeAnswerSchema = z.object({
    type: z.literal('change_answer'),
    answer: sanitizedText(50),
});
export const WordRushSkipRoundSchema = z.object({
    type: z.literal('skip_round'),
});
export const WordRushNewGameSchema = z.object({
    type: z.literal('new_game'),
});
export const WordRushSubmitClueSchema = z.object({
    type: z.literal('submit_clue'),
    clue: sanitizedText(50),
});
export const WordRushSubmitGuessSchema = z.object({
    type: z.literal('submit_guess'),
    guess: sanitizedText(50),
});
export const WordRushActionSchema = z.discriminatedUnion('type', [
    WordRushStartGameSchema,
    WordRushSubmitAnswerSchema,
    WordRushChangeAnswerSchema,
    WordRushSkipRoundSchema,
    WordRushNewGameSchema,
    WordRushSubmitClueSchema,
    WordRushSubmitGuessSchema,
]);
// =============================================================================
// Word Tiles Action Schemas
// =============================================================================
const WordTilesLetterSchema = z
    .string()
    .min(1)
    .max(1)
    .regex(/^[A-Za-z]$/)
    .transform((value) => value.toUpperCase());
const WordTilesPlacementSchema = z.object({
    row: z.number().int().min(0).max(14),
    col: z.number().int().min(0).max(14),
    rackIndex: z.number().int().min(0).max(6),
    letter: WordTilesLetterSchema.optional(),
});
export const WordTilesPlaceWordSchema = z.object({
    type: z.literal('place_word'),
    placements: z.array(WordTilesPlacementSchema).min(1).max(7),
});
export const WordTilesExchangeSchema = z.object({
    type: z.literal('exchange'),
    rackIndices: z.array(z.number().int().min(0).max(6)).min(1).max(7),
});
export const WordTilesPassSchema = z.object({
    type: z.literal('pass'),
});
export const WordTilesActionSchema = z.discriminatedUnion('type', [
    WordTilesPlaceWordSchema,
    WordTilesExchangeSchema,
    WordTilesPassSchema,
]);
// =============================================================================
// Tank Showdown Action Schemas
// =============================================================================
const TankShowdownWeaponSchema = z.enum([
    // Standard weapons
    'shell',
    'big_shell',
    'cluster_bomb',
    'digger',
    'bouncer',
    'air_strike',
    'teleport',
    'shield',
    'napalm',
    'emp',
    'earthquake',
    'sniper',
    'mirv',
    'roller',
    // Terrain weapons
    'dirt_ball',
    'big_dirt_ball',
    'cruball',
    'bouncy_dirt',
    'bunker_buster',
    'drillers',
    'well_digger',
    // Creative weapons
    'freeze_ray',
    'missile_swarm',
    'gravity_bomb',
]);
const TankShowdownTerrainPresetSchema = z.enum([
    'random',
    'hill',
    'valley',
    'cliff',
    'flats',
    'canyon',
]);
const TankShowdownSettingsSchema = z.object({
    roundsToWin: z.number().int().min(1).max(10).optional(),
    startingHp: z.number().int().min(10).max(500).optional(),
    turnTimeLimit: z.number().int().min(0).max(120).optional(),
    windEnabled: z.boolean().optional(),
    maxWindStrength: z.number().int().min(0).max(10).optional(),
    supplyDropFrequency: z.number().int().min(0).max(20).optional(),
    terrainDestructible: z.boolean().optional(),
    suddenDeath: z.boolean().optional(),
    turnLimit: z.number().int().min(0).max(200).optional(),
    terrainWidth: z.number().int().min(200).max(5000).optional(),
    terrainHeight: z.number().int().min(100).max(3000).optional(),
    terrainPreset: TankShowdownTerrainPresetSchema.optional(),
    enableBurial: z.boolean().optional(),
    enableBouncyTerrain: z.boolean().optional(),
});
export const TankShowdownStartGameSchema = z.object({
    type: z.literal('start_game'),
});
export const TankShowdownUpdateSettingsSchema = z.object({
    type: z.literal('update_settings'),
    settings: TankShowdownSettingsSchema,
});
export const TankShowdownSetAngleSchema = z.object({
    type: z.literal('set_angle'),
    angle: z.number().int().min(0).max(180),
});
export const TankShowdownSetPowerSchema = z.object({
    type: z.literal('set_power'),
    power: z.number().int().min(0).max(100),
});
export const TankShowdownSelectWeaponSchema = z.object({
    type: z.literal('select_weapon'),
    weapon: TankShowdownWeaponSchema,
});
export const TankShowdownFireSchema = z.object({
    type: z.literal('fire'),
});
export const TankShowdownSkipTurnSchema = z.object({
    type: z.literal('skip_turn'),
});
export const TankShowdownMoveLeftSchema = z.object({
    type: z.literal('move_left'),
});
export const TankShowdownMoveRightSchema = z.object({
    type: z.literal('move_right'),
});
export const TankShowdownAdvanceProjectileSchema = z.object({
    type: z.literal('advance_projectile'),
});
export const TankShowdownNextRoundSchema = z.object({
    type: z.literal('next_round'),
});
export const TankShowdownActionSchema = z.discriminatedUnion('type', [
    TankShowdownStartGameSchema,
    TankShowdownUpdateSettingsSchema,
    TankShowdownSetAngleSchema,
    TankShowdownSetPowerSchema,
    TankShowdownSelectWeaponSchema,
    TankShowdownFireSchema,
    TankShowdownSkipTurnSchema,
    TankShowdownMoveLeftSchema,
    TankShowdownMoveRightSchema,
    TankShowdownAdvanceProjectileSchema,
    TankShowdownNextRoundSchema,
]);
// =============================================================================
// Word Spies Action Schemas
// =============================================================================
const TeamSchema = z.enum(['red', 'blue']);
export const WordSpiesJoinTeamSchema = z.object({
    type: z.literal('join_team'),
    team: TeamSchema,
});
export const WordSpiesBecomeSpymasterSchema = z.object({
    type: z.literal('become_spymaster'),
});
export const WordSpiesStartGameSchema = z.object({
    type: z.literal('start_game'),
});
const ClueCountSchema = z.union([z.number().int().min(1).max(25), z.literal('unlimited')]);
export const WordSpiesGiveClueSchema = z.object({
    type: z.literal('give_clue'),
    word: sanitizedText(50),
    count: ClueCountSchema,
});
export const WordSpiesGuessWordSchema = z.object({
    type: z.literal('guess_word'),
    wordIndex: z.number().int().min(0).max(24),
});
export const WordSpiesEndTurnSchema = z.object({
    type: z.literal('end_turn'),
});
export const WordSpiesNewGameSchema = z.object({
    type: z.literal('new_game'),
});
export const WordSpiesActionSchema = z.discriminatedUnion('type', [
    WordSpiesJoinTeamSchema,
    WordSpiesBecomeSpymasterSchema,
    WordSpiesStartGameSchema,
    WordSpiesGiveClueSchema,
    WordSpiesGuessWordSchema,
    WordSpiesEndTurnSchema,
    WordSpiesNewGameSchema,
]);
// =============================================================================
// Hidden Roles Action Schemas
// =============================================================================
export const HiddenRolesStartGameSchema = z.object({
    type: z.literal('start_game'),
});
export const HiddenRolesSelectTargetSchema = z.object({
    type: z.literal('select_target'),
    targetId: z.string().min(1),
});
export const HiddenRolesInvestigateSchema = z.object({
    type: z.literal('investigate'),
    targetId: z.string().min(1),
});
export const HiddenRolesProtectSchema = z.object({
    type: z.literal('protect'),
    targetId: z.string().min(1),
});
export const HiddenRolesMediumAskSchema = z.object({
    type: z.literal('medium_ask'),
    targetId: z.string().min(1),
});
export const HiddenRolesCastVoteSchema = z.object({
    type: z.literal('cast_vote'),
    targetId: z.string().min(1),
});
export const HiddenRolesSkipVoteSchema = z.object({
    type: z.literal('skip_vote'),
});
export const HiddenRolesReadySchema = z.object({
    type: z.literal('ready'),
});
export const HiddenRolesNewGameSchema = z.object({
    type: z.literal('new_game'),
});
export const HiddenRolesActionSchema = z.discriminatedUnion('type', [
    HiddenRolesStartGameSchema,
    HiddenRolesSelectTargetSchema,
    HiddenRolesInvestigateSchema,
    HiddenRolesProtectSchema,
    HiddenRolesMediumAskSchema,
    HiddenRolesCastVoteSchema,
    HiddenRolesSkipVoteSchema,
    HiddenRolesReadySchema,
    HiddenRolesNewGameSchema,
]);
// =============================================================================
// Block Battle Action Schemas
// =============================================================================
export const BlockBattleStartGameSchema = z.object({
    type: z.literal('start_game'),
});
export const BlockBattleUpdateSettingsSchema = z.object({
    type: z.literal('update_settings'),
    mode: z.enum(['classic', 'timed']).optional(),
    timeLimit: z.number().min(0).nullable().optional(),
    startingLevel: z.number().int().min(1).optional(),
    specialBlockFrequency: z.number().int().min(0).max(100).optional(),
    garbageCancelling: z.boolean().optional(),
    ghostPiece: z.boolean().optional(),
    holdEnabled: z.boolean().optional(),
});
export const BlockBattleMoveSchema = z.object({
    type: z.literal('move'),
    direction: z.enum(['left', 'right']),
});
export const BlockBattleRotateSchema = z.object({
    type: z.literal('rotate'),
    direction: z.enum(['cw', 'ccw']),
});
export const BlockBattleSoftDropSchema = z.object({
    type: z.literal('soft_drop'),
});
export const BlockBattleHardDropSchema = z.object({
    type: z.literal('hard_drop'),
});
export const BlockBattleHoldSchema = z.object({
    type: z.literal('hold'),
});
export const BlockBattleUseItemSchema = z.object({
    type: z.literal('use_item'),
    targetId: z.string().min(1),
});
export const BlockBattleTickSchema = z.object({
    type: z.literal('tick'),
});
export const BlockBattleActionSchema = z.discriminatedUnion('type', [
    BlockBattleStartGameSchema,
    BlockBattleUpdateSettingsSchema,
    BlockBattleMoveSchema,
    BlockBattleRotateSchema,
    BlockBattleSoftDropSchema,
    BlockBattleHardDropSchema,
    BlockBattleHoldSchema,
    BlockBattleUseItemSchema,
    BlockBattleTickSchema,
]);
/** Map from game ID to action schema - eliminates need for switch statement */
const GAME_ACTION_SCHEMAS = {
    trivia: TriviaActionSchema,
    'tic-tac-toe': TicTacToeActionSchema,
    checkers: CheckersActionSchema,
    chess: ChessActionSchema,
    'texas-holdem': TexasHoldemActionSchema,
    'prompt-party': PromptPartyActionSchema,
    'sea-strike': SeaStrikeActionSchema,
    'hot-takes': HotTakesActionSchema,
    'tall-tales': TallTalesActionSchema,
    'word-rush': WordRushActionSchema,
    'word-tiles': WordTilesActionSchema,
    'word-spies': WordSpiesActionSchema,
    'tank-showdown': TankShowdownActionSchema,
    'hidden-roles': HiddenRolesActionSchema,
    'block-battle': BlockBattleActionSchema,
};
/**
 * Validates an action payload against the appropriate schema for a game type.
 * Returns the validated data on success, or an error message on failure.
 */
export function validateActionPayload(gameType, action, data) {
    try {
        // Validate game type first
        const gameIdResult = GameIdSchema.safeParse(gameType);
        if (!gameIdResult.success) {
            return { success: false, error: `Unknown game type: ${gameType}` };
        }
        // Build the payload from action and data
        const payload = { type: action, ...data };
        // Get schema from map (type-safe - compiler ensures all games are covered)
        const schema = GAME_ACTION_SCHEMAS[gameIdResult.data];
        const result = schema.safeParse(payload);
        if (result.success) {
            return { success: true, data: result.data };
        }
        else {
            const errors = result.error.errors
                .map((e) => `${e.path.join('.')}: ${e.message}`)
                .join(', ');
            return { success: false, error: `Invalid payload: ${errors}` };
        }
    }
    catch (error) {
        return {
            success: false,
            error: `Validation error: ${error instanceof Error ? error.message : String(error)}`,
        };
    }
}
//# sourceMappingURL=game-actions.js.map